import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import { RTRequest, RTRequestStatusId } from "@eres/rtregistrationapi/entities";
import { RTRegistrationApi } from "@eres/rtregistrationapi";
import { getParticipantID } from "@10x/sso";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import Strings from "@my-dld/assets/strings";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { DateUtil } from "@eres/core";
import { getRTRequestStatus, getRTRequestStatusClass } from "@my-dld/utils/ProcedureUtils";
import I8N from "@eres/core/dist/I8N";
import ConfirmationPopup from "@my-dld/components/Popup/ConfirmationPopup";
import ListGridDataView from "@my-dld/components/DataView/ListGridDataView";
import RTRequestsListView from "@my-dld/components/MyRequest/RTRequestsListView";
import RTRequestsGridView from "../../../components/MyRequest/RTRequestsGridView";
import { formatAppDate } from "@my-dld/utils/Utils";

interface Props extends RoutedViewProps {

}

interface State {
	isLoading: boolean;
	rtRequests: RTRequest[];
	isAnyPending?: boolean;
	selRequest?: RTRequest;
}

class RTRequests extends Component<Props, State> {
	constructor( props: Props ) {
		super( props );
		this.state = {
			isLoading: true,
			rtRequests: [],
		};
	}

	private static trackEvent( isSuccess: boolean, request: RTRequest ) {
		const action = isSuccess ? "CancelProcedureSuccess" : "CancelProcedureFail";
		trackEventWithData( "RTRegistration", action, {
			procedureId: "" + request.ProcedureId,
			tabuPropertyId: "" + request.PropertyId,
			procedureNumber: ( request.ProcedureNumber + "/" + request.ProcedureYear ),
		} );
	}

	componentDidMount() {
		this.loadRTRequests();
	}

	render() {
		const { isLoading, rtRequests, selRequest } = this.state;
		return <React.Fragment>
			<ListGridDataView
				isLoading={ isLoading }
				dataAvailable={ rtRequests.length > 0 }
				title={ Strings.rt_office_transaction_requests }
				renderListView={ this.renderListView }
				renderGridView={ this.renderGridView }/>
			{ selRequest && <ConfirmationPopup
				message={ Strings.confirm_cancel_procedure }
				onConfirm={ () => this.onCancelRTRequest( selRequest ) }
				onCancel={ () => {
					this.setState( { selRequest: undefined } );
				} }/>
			}
		</React.Fragment>;
	}

	private renderListView = () => {
		const { rtRequests, isAnyPending } = this.state;
		return <RTRequestsListView
			isAnyPending={ isAnyPending }
			items={ rtRequests }
			onShowCancel={ this.showCancel }/>;
	};

	private renderGridView = () => {
		const { rtRequests } = this.state;
		return <RTRequestsGridView
			items={ rtRequests }
			onShowCancel={ this.showCancel }/>;
	};

	private showCancel = ( item: RTRequest ) => {
		this.setState( {
			selRequest: item
		} );
	};

	private loadRTRequests() {
		RTRegistrationApi.loadRTRequests( getParticipantID()! ).then( ( resp: any ) => {
			if ( resp.ValidationErrorsList && resp.ValidationErrorsList.length > 0 ) {
				this.setState( { isLoading: false } );
				showErrorMessage( RTRegistrationApi.getValidationError( resp.ValidationErrors ) );
			} else {
				const requests = resp.ProcedureEnquiryDetail ? resp.ProcedureEnquiryDetail : [];
				this.setState( {
					isLoading: false,
					rtRequests: requests,
					isAnyPending: requests.find( ( request: RTRequest ) =>
						request.OnlineRequestStatusId === RTRequestStatusId.PENDING ),
				} );
			}
		} ).catch( reason => {
			this.setState( { isLoading: false } );
			showErrorMessage( reason );
		} );
	}

	private onCancelRTRequest = ( request: RTRequest ) => {
		showLoading();
		RTRegistrationApi.cancelRTRequest( request.ProcedureId, request.ProcedureYear, request.ProcedureNumber,
			request.OnlineRequestId, getParticipantID()! ).then( ( resp: any ) => {
			hideLoading();
			if ( resp.ValidationErrorsList && resp.ValidationErrorsList.length > 0 ) {
				RTRequests.trackEvent( false, request );
				showErrorMessage( RTRegistrationApi.getValidationError( resp.ValidationErrors ) );
			} else {
				RTRequests.trackEvent( true, request );
				this.setState( {
					rtRequests: this.state.rtRequests!.map( ( req: RTRequest ) => {
						return ( request.OnlineRequestId === req.OnlineRequestId ) ?
							{ ...req, OnlineRequestStatusId: RTRequestStatusId.CANCEL } : req;
					} ),
				} );
			}
		} ).catch( reason => {
			hideLoading();
			showErrorMessage( reason );
		} );
	};
}

export function getRTReqTransactionNo( item: RTRequest ) {
	return item.ProcedureNumber + "/" + item.ProcedureYear;
}

export function getRTReqProcedureType( item: RTRequest ) {
	return I8N.isRTL() ? item.ProcedureTypeAr : item.ProcedureTypeEn;
}

export function getRTReqStatus( item: RTRequest ) {
	return getRTRequestStatus( item.OnlineRequestStatusId );
}

export function getRTReqStatusClass( item: RTRequest ) {
	return getRTRequestStatusClass( item.OnlineRequestStatusId );
}

export function getRTReqDate( item: RTRequest ) {
	const date = DateUtil.parseDate( item.CreationDate, "DD/MM/YYYY hh:mm:ss tt" );
	return formatAppDate( date, "en" );
}

export default RTRequests;
